export {
  formatDate,
  formatDateKey,
  formatDateTime,
  formatTime,
  getLocaleSettings,
  setLocaleSettings
} from './localeFormat';

// Format weight with unit. Default display is Ton (data is stored in kg).
// Pass noUnit: true when the value is under a label that already shows the unit (e.g. column header "Weight (tonne)").
export const formatWeight = (weight, unit = 'ton', options = {}) => {
  if (weight === null || weight === undefined) return '';
  const numericWeight = parseFloat(weight);
  if (Number.isNaN(numericWeight)) return '';

  const normalizedUnit = String(unit || 'ton').toLowerCase();
  const localeOpts = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  const formatted = normalizedUnit === 'ton' || normalizedUnit === 'tons' || normalizedUnit === 'tonne' || normalizedUnit === 't'
    ? (numericWeight / 1000).toLocaleString('fr-FR', localeOpts)
    : numericWeight.toLocaleString('fr-FR', localeOpts);

  if (options.noUnit) return formatted;
  if (normalizedUnit === 'ton' || normalizedUnit === 'tons' || normalizedUnit === 'tonne' || normalizedUnit === 't') {
    return `${formatted} Ton`;
  }
  return `${formatted} ${unit}`;
};

/** True when tare (load or truck) and loaded gross exist and gross > tare (stored in kg). */
export const loadHasWeightsForCharged = (load) => {
  if (!load) return false;
  const emptyWeight = load.emptyWeight ?? load.truck?.emptyWeight;
  const loadedWeight = load.loadedWeight;
  if (emptyWeight == null || loadedWeight == null) return false;
  const e = parseFloat(emptyWeight);
  const l = parseFloat(loadedWeight);
  return Number.isFinite(e) && Number.isFinite(l) && l > e;
};

// Format currency (FCFA)
export const formatCurrency = (amount, currency = 'XOF') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format percentage
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '';
  return `${parseFloat(value).toFixed(decimals)}%`;
};

// Capitalize first letter
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Convert camelCase to Title Case
export const camelToTitle = (str) => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Get status badge class
export const getStatusBadgeClass = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'active':
    case 'completed':
    case 'paid':
    case 'success':
      return 'status-active';
    case 'inactive':
    case 'failed':
    case 'cancelled':
    case 'overdue':
      return 'status-inactive';
    case 'pending':
    case 'processing':
      return 'status-pending';
    default:
      return 'status-pending';
  }
};

// Get transaction type display
export const getTransactionTypeDisplay = (type) => {
  switch (type) {
    case 'IMPORT':
      return 'Import';
    case 'EXPORT':
      return 'Export';
    default:
      return type;
  }
};

// Get payment method display
export const getPaymentMethodDisplay = (method) => {
  switch (method) {
    case 'CASH':
      return 'Cash';
    case 'CREDIT_CARD':
      return 'Credit Card';
    case 'WAVE':
      return 'Wave Money';
    case 'ORANGE_MONEY':
      return 'Orange Money';
    case 'BANK_TRANSFER':
      return 'Bank Transfer';
    case 'CREDIT':
      return 'Credit (Bon de Pesage)';
    case 'PREPAID':
      return 'Prepaid balance';
    default:
      return method;
  }
};

// Get user role display
export const getUserRoleDisplay = (role) => {
  // Import i18n inside the function to avoid circular dependency
  const i18n = require('../i18n').default;
  return i18n.t(`roles.${role}`) || role;
};

// Get client size display
export const getClientSizeDisplay = (size) => {
  switch (size) {
    case 'SMALL':
      return 'Small';
    case 'MEDIUM':
      return 'Medium';
    case 'LARGE':
      return 'Large';
    default:
      return size;
  }
};

// Validate email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number (basic)
export const isValidPhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

// Generate random ID
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Debounce function
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Download file from blob
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** Normalize axios/binary payload to a PDF Blob. */
export function ensurePdfBlob(data) {
  if (data instanceof Blob) {
    return data.type === 'application/pdf'
      ? data
      : new Blob([data], { type: 'application/pdf' });
  }
  return new Blob([data], { type: 'application/pdf' });
}

/**
 * Open a PDF in a new tab using the browser PDF viewer (same approach as bills/certificates).
 * @returns {Promise<boolean>} false only if pop-up blocked and download fallback could not run
 */
export async function openPdfInNewTab(data, filename = 'document.pdf') {
  const blob = ensurePdfBlob(data);
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  const signature = String.fromCharCode(head[0], head[1], head[2], head[3]);
  if (signature !== '%PDF') {
    throw new Error('Invalid PDF file');
  }

  const url = window.URL.createObjectURL(blob);
  // Do not use noopener: the new tab must be allowed to load this blob URL.
  const win = window.open(url, '_blank');
  if (win) {
    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    return true;
  }

  window.URL.revokeObjectURL(url);
  const safeName = String(filename || 'document.pdf').replace(/[^\w.\- ]+/g, '_');
  const name = safeName.toLowerCase().endsWith('.pdf') ? safeName : `${safeName}.pdf`;
  downloadFile(blob, name);
  return true;
}

// Copy to clipboard
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch (err) {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

// Calculate percentage change
export const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === 0) return newValue > 0 ? 100 : 0;
  return ((newValue - oldValue) / oldValue) * 100;
};

// Get pagination info
export const getPaginationInfo = (total, page, limit) => {
  const totalPages = Math.ceil(total / limit);
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return {
    totalPages,
    hasNextPage,
    hasPrevPage,
    startItem,
    endItem,
  };
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Sort array by property
export const sortBy = (array, property, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = a[property];
    const bVal = b[property];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Group array by property
export const groupBy = (array, property) => {
  return array.reduce((groups, item) => {
    const key = item[property];
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
};

// Calculate weight in tons
export const convertToTons = (weightInKg) => {
  return weightInKg / 1000;
};

// Calculate revenue from weight and rate
export const calculateRevenue = (weightInKg, ratePerTon) => {
  return convertToTons(weightInKg) * ratePerTon;
};

// Format distance with units
export const formatDistance = (distanceKm) => {
  if (!distanceKm || distanceKm === 0) return '0 km';
  
  if (distanceKm < 1) {
    return `${(distanceKm * 1000).toFixed(0)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};

// Format speed with units
export const formatSpeed = (speedKmh) => {
  if (!speedKmh || speedKmh === 0) return '0 km/h';
  return `${speedKmh.toFixed(0)} km/h`;
};

/** Display name from a user object (API nested user). */
export const formatUserDisplayName = (user) => {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.username || '';
};

/**
 * Who verified the payment: approver when set, else processor for completed payments
 * (e.g. staff-recorded completed without separate approval step).
 */
export const paymentVerifierDisplayName = (payment) => {
  if (!payment) return '';
  if (payment.approvedByUser) return formatUserDisplayName(payment.approvedByUser);
  if (payment.status === 'COMPLETED' && payment.processedByUser) {
    return formatUserDisplayName(payment.processedByUser);
  }
  return '';
};

/** Clients linked to a truck via transporteurs (for driver dashboard / my trucks). */
export const getLinkedClientsFromTruck = (truck) => {
  if (!truck) return [];
  const out = [];
  const seen = new Set();
  const push = (c) => {
    if (!c?.id || seen.has(c.id)) return;
    seen.add(c.id);
    out.push(c);
  };
  if (truck.client) push(truck.client);
  for (const tt of truck.truckTransporteurs || []) {
    for (const tc of tt.transporteur?.transporteurClients || []) {
      if (tc.client) push(tc.client);
    }
  }
  return out;
};

/** Labels for drivers assigned to a truck (Driver entity + legacy User via driver_trucks). */
export const getAssignedDriverDisplayLines = (truck) => {
  if (!truck) return [];
  const lines = [];
  const seenKeys = new Set();

  const labelFromNames = (d, fallbackId) => {
    const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
    return name || d.phone || d.username || fallbackId;
  };

  const pushLine = (dedupeKey, label) => {
    const t = String(label || '').trim();
    if (!t || seenKeys.has(dedupeKey)) return;
    seenKeys.add(dedupeKey);
    lines.push(t);
  };

  const assignedUserIds = new Set(
    (truck.assignedDrivers || []).map((r) => r.driver?.id).filter(Boolean)
  );

  // Prefer Driver rows first; merge with User when Driver.userId points at the same account
  for (const row of truck.truckDrivers || []) {
    const d = row.driver;
    if (!d?.id) continue;
    const label = labelFromNames(d, d.id);
    const key =
      d.userId && assignedUserIds.has(d.userId) ? `account:${d.userId}` : `driver:${d.id}`;
    pushLine(key, label);
  }

  for (const row of truck.assignedDrivers || []) {
    const d = row.driver;
    if (!d?.id) continue;
    if (seenKeys.has(`account:${d.id}`)) continue;
    const label = labelFromNames(d, d.id);
    pushLine(`account:${d.id}`, label);
  }

  // Same person sometimes appears twice without userId link; collapse identical labels (case-insensitive)
  const out = [];
  const seenNorm = new Set();
  for (const line of lines) {
    const norm = line.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!norm || seenNorm.has(norm)) continue;
    seenNorm.add(norm);
    out.push(line);
  }
  return out;
};